import React, { useState } from 'react';
import { 
    Building2, 
    User, 
    Rocket, 
    ArrowRight, 
    ArrowLeft, 
    Check, 
    Activity, 
    Globe, 
    ShieldCheck, 
    Target,
    Zap
} from 'lucide-react';
import { apiClient } from '../utils/api';
import { industries, companySizes, roles } from '../utils/constants';
import logo from '../Logo/logo.png';
import './OnboardingPage.css';

export default function OnboardingPage({ company, onComplete }) {
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        company_name: company.company_name || company.name || '',
        industry: '',
        company_size: '',
        role: '',
        phone: '',
        website: '',
    });

    const update = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    const canNext = () => {
        if (step === 1) return form.company_name.trim(); // Only company name is mandatory
        return true;
    };

    const nextStep = () => {
        if (canNext()) setStep(s => s + 1);
    };

    const prevStep = () => {
        setStep(s => s - 1);
    };

    const handleSubmit = async () => {
        setSaving(true);
        try {
            const data = await apiClient.onboardCompany(company.id, {
                ...form,
                onboarding_complete: true,
            });
            if (data.success || data.company) {
                onComplete(data.company || { ...company, ...form, onboarding_complete: true });
            }
        } catch (err) {
            console.error('Onboarding failed:', err);
        } finally {
            setSaving(false);
        }
    };

    const getSidebarContent = () => {
        switch (step) {
            case 1:
                return {
                    title: "Welcome to BharatQA",
                    desc: "Let's start by setting up your organization profile. This helps us personalize your bug reporting experience.",
                    features: [
                        { icon: Building2, text: "Industry optimized testing" },
                        { icon: Globe, text: "Global targeting options" }
                    ]
                };
            case 2:
                return {
                    title: "About Your Team",
                    desc: "Knowing your role and team size helps us provide the most relevant documentation and dashboard widgets.",
                    features: [
                        { icon: Target, text: "Tailored QA workflows" },
                        { icon: ShieldCheck, text: "Role-based permissions" }
                    ]
                };
            case 3:
                return {
                    title: "Ready to Launch",
                    desc: "Your profile is almost ready. Review your details and let's get started with your first project.",
                    features: [
                        { icon: Zap, text: "Get results in < 24 hrs" },
                        { icon: Activity, text: "Real-time bug tracking" }
                    ]
                };
            default:
                return {};
        }
    };

    const content = getSidebarContent();

    return (
        <div className="onboarding-page">
            <div className="onboarding-container">
                {/* Sidebar */}
                <aside className="onboarding-sidebar">
                    <div className="top-section">
                        <div className="onboarding-brand">
                            <img src={logo} alt="BharatQA" />
                            <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: -0.5 }}>BHARAT<span style={{color: 'var(--saffron)'}}>QA</span></span>
                        </div>
                        <div className="sidebar-welcome-text">
                            <h1>{content.title}</h1>
                            <p>{content.desc}</p>
                        </div>
                        <div className="sidebar-features">
                            {content.features.map((f, i) => (
                                <div key={i} className="feature-item">
                                    <div className="feature-icon"><f.icon size={16} /></div>
                                    <span>{f.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <div className="onboarding-meta" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        © 2026 BharatQA. India's Premier Testing Platform.
                    </div>
                </aside>

                {/* Main Content */}
                <main className="onboarding-main">
                    <div className="onboarding-header">
                        <div className="onboarding-step-indicator">
                            {[1, 2, 3].map(s => (
                                <div key={s} className="step-pill">
                                    <div 
                                        className="step-pill-fill" 
                                        style={{ width: step >= s ? '100%' : '0%' }}
                                    />
                                </div>
                            ))}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--saffron)', textTransform: 'uppercase', letterSpacing: 1 }}>
                            Step {step} of 3
                        </div>
                    </div>

                    <div className="onboarding-step-body">
                        {step === 1 && (
                            <div className="onboarding-step-content">
                                <h2>Your Company</h2>
                                <p className="subtitle">Tell us about where you work.</p>

                                <div className="onboarding-form">
                                    <div className="form-field">
                                        <label>Company Name</label>
                                        <input 
                                            type="text" 
                                            className="onboarding-input"
                                            placeholder="e.g. Acme Corp"
                                            value={form.company_name}
                                            onChange={e => update('company_name', e.target.value)}
                                        />
                                    </div>

                                    <div className="form-field">
                                        <label>Industry <span className="optional-tag">(Optional)</span></label>
                                        <div className="onboarding-grid">
                                            {industries.map(ind => (
                                                <div 
                                                    key={ind}
                                                    className={`selection-chip ${form.industry === ind ? 'active' : ''}`}
                                                    onClick={() => update('industry', ind)}
                                                >
                                                    <div className="chip-dot" />
                                                    {ind}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="form-field">
                                        <label>Company Website <span className="optional-tag">(Optional)</span></label>
                                        <input 
                                            type="url" 
                                            className="onboarding-input"
                                            placeholder="https://acme.com"
                                            value={form.website}
                                            onChange={e => update('website', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="onboarding-step-content">
                                <h2>Professional Profile</h2>
                                <p className="subtitle">Let's tailor the platform to your needs.</p>

                                <div className="onboarding-form">
                                    <div className="form-field">
                                        <label>Company Size <span className="optional-tag">(Optional)</span></label>
                                        <div className="onboarding-grid">
                                            {companySizes.map(size => (
                                                <div 
                                                    key={size}
                                                    className={`selection-chip ${form.company_size === size ? 'active' : ''}`}
                                                    onClick={() => update('company_size', size)}
                                                >
                                                    <div className="chip-dot" />
                                                    {size}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="form-field">
                                        <label>Your Primary Role <span className="optional-tag">(Optional)</span></label>
                                        <div className="onboarding-grid">
                                            {roles.map(role => (
                                                <div 
                                                    key={role}
                                                    className={`selection-chip ${form.role === role ? 'active' : ''}`}
                                                    onClick={() => update('role', role)}
                                                >
                                                    <div className="chip-dot" />
                                                    {role}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="form-field">
                                        <label>Contact Number <span className="optional-tag">(Optional)</span></label>
                                        <input 
                                            type="tel" 
                                            className="onboarding-input"
                                            placeholder="+91 00000 00000"
                                            value={form.phone}
                                            onChange={e => update('phone', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="onboarding-step-content">
                                <div className="celebration-icon">
                                    <Check size={32} />
                                </div>
                                <h2>Review & Finish</h2>
                                <p className="subtitle">Everything looks great. One last look?</p>

                                <div className="summary-container">
                                    <div className="summary-card">
                                        <span className="label">Organization</span>
                                        <span className="value">{form.company_name}</span>
                                    </div>
                                    <div className="summary-card">
                                        <span className="label">Industry</span>
                                        <span className="value">{form.industry}</span>
                                    </div>
                                    <div className="summary-card">
                                        <span className="label">Team Size</span>
                                        <span className="value">{form.company_size}</span>
                                    </div>
                                    <div className="summary-card">
                                        <span className="label">Role</span>
                                        <span className="value">{form.role}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <footer className="onboarding-footer">
                        {step > 1 ? (
                            <button className="btn-back" onClick={prevStep}>
                                <ArrowLeft size={16} /> Previous
                            </button>
                        ) : <div />}

                        {step < 3 ? (
                            <button 
                                className="btn-next" 
                                onClick={nextStep}
                                disabled={!canNext()}
                            >
                                Continue <ArrowRight size={18} />
                            </button>
                        ) : (
                            <button 
                                className="btn-next launch-btn" 
                                onClick={handleSubmit}
                                disabled={saving}
                                style={{ 
                                    background: saving ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #10b981, #059669)',
                                    boxShadow: saving ? 'none' : '0 8px 16px rgba(16, 185, 129, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.4)'
                                }}
                            >
                                {saving ? 'Finalizing...' : 'Launch Platform'}
                                <Rocket size={18} />
                            </button>
                        )}
                    </footer>
                </main>
            </div>
        </div>
    );
}